import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'lib/request/api';
import type {
  CaptureJobEnvelope,
  CaptureResult,
  EphemeralScenarioDefinition,
} from 'types/dps-capture';

/** Default debounce window for the recompute (03-CAP §5). */
export const CAPTURE_DEBOUNCE_MS = 500;

const CAPTURE_PATH = 'development_pace_scenarios/capture_previews';

/** Poll cadence for the async capture compute (INS-11297). */
const POLL_INTERVAL_MS = 1_000;
/**
 * ~90s budget. The compute is org-wide PostGIS work — a cold run is slow but bounded — so
 * beyond this the preview surfaces an error rather than polling forever.
 */
const MAX_POLL_ATTEMPTS = 90;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Type guard for the async-job envelope. A {@link CaptureResult} always carries
 * `summaries`, so its absence (plus a `jobId`) marks the 202 envelope.
 *
 * @param {CaptureResult | CaptureJobEnvelope} body - The response body.
 * @return {boolean} True when the body is the async-job envelope.
 */
function isCaptureJobEnvelope(
  body: CaptureResult | CaptureJobEnvelope
): body is CaptureJobEnvelope {
  return (
    typeof (body as CaptureJobEnvelope).jobId === 'string' &&
    !('summaries' in body)
  );
}

/**
 * POSTs the definition and, if the BE returns the async `202` envelope, polls the SAME
 * endpoint by `job_id` until the {@link CaptureResult} is ready (or the poll budget is
 * exhausted / the request aborts). A direct result (a sync/cache-hit `200`) returns at once.
 *
 * @param {EphemeralScenarioDefinition} definition - The whole draft to compute.
 * @param {AbortSignal} signal - Aborted when a newer edit supersedes this request.
 * @return {Promise<CaptureResult>} The resolved capture result.
 */
async function fetchCaptureResult(
  definition: EphemeralScenarioDefinition,
  signal: AbortSignal
): Promise<CaptureResult> {
  let body = await api
    .post(CAPTURE_PATH, { json: definition, signal })
    .json<CaptureResult | CaptureJobEnvelope>();
  let attempts = 0;
  while (isCaptureJobEnvelope(body)) {
    if (body.status === 'failed') {
      throw new Error('The capture preview computation failed.');
    }
    if (attempts >= MAX_POLL_ATTEMPTS) {
      throw new Error('The capture preview is taking too long to compute.');
    }
    attempts += 1;
    await delay(POLL_INTERVAL_MS);
    body = await api
      .post(CAPTURE_PATH, { searchParams: { job_id: body.jobId }, signal })
      .json<CaptureResult | CaptureJobEnvelope>();
  }
  return body;
}

/** What {@link useCapturePreview} returns. */
export interface UseCapturePreview {
  /** The latest applied capture result, or null before the first compute. */
  result: CaptureResult | null;
  /** True while a recompute for the current definition is pending/in flight. */
  loading: boolean;
  /** The failed compute, or null. */
  error: Error | null;
  /**
   * Re-runs the current definition. The compute is time-sensitive (a slow draft
   * can trip the request timeout — a 503), so a failed preview is recoverable
   * without editing the form. A no-op while the definition is null.
   */
  retry: () => void;
}

/**
 * Recomputes the capture preview whenever the (serialized) definition changes —
 * `POST /development_pace_scenarios/capture_previews`.
 *
 * Two guarantees (AC 7):
 *  - Debounced: rapid edits within the window collapse to ONE request (the timer
 *    resets on every change, so only the settled definition fires).
 *  - Stale-discard: a response is applied only if its definition is still the
 *    latest one requested, so a slow response for an old edit never overwrites a
 *    newer edit's result. The superseded request is also aborted.
 *
 * A null definition (no TC selected, or no area with a geometry) clears the
 * result and fires nothing.
 *
 * @param {EphemeralScenarioDefinition | null} definition - The whole draft, or null.
 * @param {number} debounceMs - Debounce window (injectable for tests).
 * @return {UseCapturePreview} The result, in-flight state, and any error.
 */
export function useCapturePreview(
  definition: EphemeralScenarioDefinition | null,
  debounceMs: number = CAPTURE_DEBOUNCE_MS
): UseCapturePreview {
  const [result, setResult] = useState<CaptureResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Bumped by retry() to force a re-run of the current (unchanged) definition.
  const [retryNonce, setRetryNonce] = useState(0);

  // The serialized definition drives the effect: identical content => identical
  // key => no refire; different content => a fresh compute keyed by that content.
  const key = definition ? JSON.stringify(definition) : null;
  // The most recently requested key — the stale-response guard reads this.
  const latestKey = useRef<string | null>(null);

  useEffect(() => {
    if (definition === null || key === null) {
      latestKey.current = null;
      setResult(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    latestKey.current = key;
    setLoading(true);
    setError(null);
    const controller = new AbortController();

    const timer = setTimeout(() => {
      fetchCaptureResult(definition, controller.signal)
        .then((data) => {
          if (latestKey.current !== key) return; // superseded by a newer edit
          setResult(data);
          setLoading(false);
        })
        .catch((requestError: Error) => {
          if (controller.signal.aborted || latestKey.current !== key) return;
          setError(requestError);
          setLoading(false);
        });
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // key captures the definition's content; definition/debounceMs are stable per key.
    // retryNonce re-runs the same key on demand (recover a timed-out draft compute).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, debounceMs, retryNonce]);

  const retry = useCallback(() => setRetryNonce((nonce) => nonce + 1), []);

  return { result, loading, error, retry };
}
